import { useEffect, useState, type ReactNode } from 'react';
import {
  ArrowClockwise,
  CaretCircleDown,
  CheckCircle,
  DotsThree,
  Export,
  PlusSquare,
  Warning,
  WarningOctagon,
} from '@phosphor-icons/react';
import type { PickResult, SlateGameDto, TeamDto } from '@pickem/shared';

/**
 * Taste primitives.
 *
 * Small, sharp and reused everywhere. Cards appear only where elevation genuinely
 * communicates hierarchy (a pick target, a discrete game); everything else is grouped with
 * hairline rules and negative space rather than being boxed for the sake of it.
 */

export function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(' ');
}

/** Section heading: left-aligned, weight and colour carry the hierarchy, not scale. */
export function SectionLabel({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <h2 className="font-mono text-[11px] font-medium uppercase tracking-[0.18em] text-field-400">
        {children}
      </h2>
      {action}
    </div>
  );
}

export function Card({
  children,
  className,
  interactive = false,
  style,
}: {
  children: ReactNode;
  className?: string;
  interactive?: boolean;
  /** Carries the `--index` custom property that drives the stagger cascade. */
  style?: React.CSSProperties;
}) {
  return (
    <div
      style={style}
      className={cx(
        'rounded-card border border-line bg-field-850',
        interactive &&
          'transition-colors duration-300 ease-[var(--ease-out-expo)] hover:border-line-strong',
        className,
      )}
    >
      {children}
    </div>
  );
}

type ButtonVariant = 'primary' | 'ghost' | 'outline' | 'danger';

export function Button({
  children,
  onClick,
  variant = 'primary',
  disabled,
  loading,
  full,
  size = 'md',
  type = 'button',
  className,
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: ButtonVariant;
  disabled?: boolean;
  loading?: boolean;
  full?: boolean;
  size?: 'sm' | 'md' | 'lg';
  type?: 'button' | 'submit';
  className?: string;
}) {
  const variants: Record<ButtonVariant, string> = {
    primary: 'bg-signal text-ink hover:bg-signal-glow disabled:bg-field-700 disabled:text-field-400',
    ghost: 'text-field-300 hover:bg-field-800 hover:text-field-100',
    outline: 'border border-line text-field-200 hover:border-line-strong hover:bg-field-800',
    danger: 'border border-loss/40 text-loss hover:bg-loss/10',
  };
  const sizes = {
    sm: 'h-9 px-3 text-[13px]',
    md: 'h-11 px-4 text-sm',
    lg: 'h-14 px-6 text-base',
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={cx(
        'inline-flex items-center justify-center gap-2 rounded-xl font-medium',
        'transition-all duration-200 ease-[var(--ease-out-expo)]',
        // Tactile press: a physical push confirms the tap landed.
        'active:scale-[0.98] disabled:cursor-not-allowed disabled:active:scale-100',
        variants[variant],
        sizes[size],
        full && 'w-full',
        className,
      )}
    >
      {loading && <ArrowClockwise size={16} weight="bold" className="animate-spin" />}
      {children}
    </button>
  );
}

/** Result semantics. Deliberately not the brand accent. */
export function ResultPill({ result, compact }: { result: PickResult; compact?: boolean }) {
  const map: Record<PickResult, { label: string; className: string }> = {
    win: { label: 'Won', className: 'bg-win/15 text-win border-win/30' },
    loss: { label: 'Lost', className: 'bg-loss/15 text-loss border-loss/30' },
    push: { label: 'Push', className: 'bg-push/15 text-push border-push/30' },
    void: { label: 'Void', className: 'bg-field-800 text-field-400 border-line' },
    pending: { label: 'Pending', className: 'bg-field-800 text-field-400 border-line' },
  };
  const { label, className } = map[result];
  return (
    <span
      className={cx(
        'inline-flex items-center rounded-md border font-mono uppercase tracking-wider',
        compact ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-1 text-[11px]',
        className,
      )}
    >
      {label}
    </span>
  );
}

/**
 * Team mark.
 *
 * Renders the club's real crest when the feed supplied one, and falls back to a monogram
 * when it did not — which is not a rare edge case: roughly one college team in nine has no
 * logo on file, usually an FCS side appearing as an opponent. The fallback also catches a
 * crest that 404s or fails to decode, so a broken image never leaves a hole in the row.
 *
 * WHY THE PLATE IS LIGHT
 * ESPN's crests are the standard colour artwork, drawn to sit on white. A great many of
 * them are predominantly black or navy — the Raiders, the Jaguars, Penn State, most of the
 * Big Ten — and on a near-black field those are invisible. A dark translucent plate does
 * not fix it: it is still dark, so a dark crest still disappears into it.
 *
 * So the plate is a light chip tinted with the team's own primary colour. The tint carries
 * identity and the lightness carries legibility, and because *any* hue mixed this far
 * toward white lands light, it is legible for every team without special-casing a single
 * one. The monogram fallback uses the same chip so the two are interchangeable in a list
 * without disturbing its rhythm.
 */
export function TeamMark({
  team,
  size = 40,
  dim = false,
}: {
  team: TeamDto;
  size?: number;
  dim?: boolean;
}) {
  const [failed, setFailed] = useState(false);
  const showLogo = Boolean(team.logoUrl) && !failed;

  // Reset when the row is reused for a different team, or a previous failure would stick
  // to whoever lands in this slot next.
  useEffect(() => setFailed(false), [team.logoUrl]);

  const plate = tintToward(team.primaryColor, 0.18);
  const chrome = {
    width: size,
    height: size,
    background: plate,
    // A hairline of the team's own colour rather than a generic stroke: it separates the
    // chip from the card without introducing a colour that belongs to nobody.
    boxShadow: `inset 0 0 0 1px ${tintToward(team.primaryColor, 0.34)}`,
  } as const;

  if (showLogo) {
    return (
      <span
        className={cx(
          'grid shrink-0 place-items-center overflow-hidden rounded-lg transition-opacity duration-300',
          dim && 'opacity-40',
        )}
        style={chrome}
      >
        <img
          src={team.logoUrl as string}
          alt=""
          aria-hidden
          width={size}
          height={size}
          loading="lazy"
          decoding="async"
          onError={() => setFailed(true)}
          style={{
            // Inset so the crest never touches the plate edge.
            width: size * 0.76,
            height: size * 0.76,
            objectFit: 'contain',
          }}
        />
      </span>
    );
  }

  return (
    <span
      aria-hidden
      className={cx(
        'grid shrink-0 place-items-center rounded-lg font-mono font-semibold transition-opacity duration-300',
        dim && 'opacity-40',
      )}
      style={{
        ...chrome,
        color: readableOn(plate),
        fontSize: size * 0.3,
        letterSpacing: '0.02em',
      }}
    >
      {team.abbreviation.slice(0, 4)}
    </span>
  );
}

function parseHex(hex: string | null | undefined): [number, number, number] | null {
  if (!hex) return null;
  const value = hex.replace('#', '').trim();
  if (!/^[0-9a-f]{6}$/i.test(value)) return null;
  return [
    parseInt(value.slice(0, 2), 16),
    parseInt(value.slice(2, 4), 16),
    parseInt(value.slice(4, 6), 16),
  ];
}

/**
 * Mixes a team colour toward white, keeping `amount` of the original.
 *
 * At the ratios used here every hue — including pure black — lands light, which is the
 * point: the result has to be a legible background for dark artwork no matter what the
 * feed reports as the team's colour.
 */
function tintToward(hex: string | null | undefined, amount: number): string {
  const rgb = parseHex(hex);
  if (!rgb) return amount > 0.25 ? '#D8DCE1' : '#EDEFF2';
  const [r, g, b] = rgb.map((c) => Math.round(255 - (255 - c) * amount));
  return `rgb(${r} ${g} ${b})`;
}

/** Picks a legible foreground for a background colour using perceived luminance. */
function readableOn(color: string): string {
  const rgb = parseHex(color) ?? rgbTriplet(color);
  if (!rgb) return '#F5F6F7';
  const [r, g, b] = rgb;
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? '#0B0D0F' : '#F5F6F7';
}

function rgbTriplet(color: string): [number, number, number] | null {
  const match = color.match(/rgb\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)/i);
  return match ? [Number(match[1]), Number(match[2]), Number(match[3])] : null;
}

/**
 * A matchup on one line: crest, name and line for each side, home team first.
 *
 * Home-first with `vs` rather than the broadcast's away-first `@`. The commissioner asked
 * for home first, and `@` read the other way round would actively assert the wrong thing —
 * "Chiefs @ Raiders" means the Chiefs are travelling, so home-first plus `@` says the home
 * team is the visitor. `vs` carries the same pairing without claiming a direction, and it
 * is already the right word for the neutral-site games in the college pool.
 */
export function MatchupRow({
  home,
  away,
  size = 26,
  className,
}: {
  home: MatchupSideProps;
  away: MatchupSideProps;
  size?: number;
  className?: string;
}) {
  return (
    <div className={cx('flex items-center gap-2', className)}>
      <MatchupSide {...home} size={size} />
      <span className="shrink-0 font-mono text-[10px] uppercase tracking-[0.12em] text-field-600">
        vs
      </span>
      <MatchupSide {...away} size={size} />
    </div>
  );
}

interface MatchupSideProps {
  team: TeamDto;
  /** `-4.5`, `+3`, `PK`. Null when the game has no line yet. */
  lineLabel: string | null;
  isFavorite?: boolean;
}

function MatchupSide({
  team,
  lineLabel,
  isFavorite,
  size,
}: MatchupSideProps & { size: number }) {
  return (
    // `min-w-0` is what lets the name truncate instead of forcing the row wider than the
    // card — college names are long and two of them share one phone width.
    <div className="flex min-w-0 flex-1 items-center gap-2">
      <TeamMark team={team} size={size} />
      <span className="min-w-0 truncate text-[13px] leading-tight text-field-200">
        {team.shortName}
      </span>
      {lineLabel && (
        <span
          className={cx(
            'shrink-0 font-mono text-[13px] font-semibold tabular-nums',
            isFavorite ? 'text-field-100' : 'text-field-400',
          )}
        >
          {lineLabel}
        </span>
      )}
    </div>
  );
}

/**
 * Where the API lives, from the browser's point of view.
 *
 * Empty whenever the web app and the API share an origin, which is the normal case: the
 * dev server proxies `/api`, and a production deployment should rewrite it. Reading the
 * env directly keeps this inside the presentation layer — it is configuration, not data
 * access, so it does not cross the boundary that `check:layers` protects.
 */
const API_ORIGIN: string = import.meta.env.VITE_API_URL ?? '';

/**
 * A player's profile picture, or their initials.
 *
 * Initials are the normal state, not a degraded one — most people never upload anything —
 * so they get a real treatment rather than a grey silhouette: a stable colour derived from
 * the user's own id, so the same person is the same colour on every screen and in every
 * session without anything being stored to make it so.
 */
export function Avatar({
  userId,
  displayName,
  url,
  size = 32,
  ring = false,
}: {
  userId: string;
  displayName: string;
  url: string | null;
  size?: number;
  /** Accent ring, used to mark the signed-in player in a list. */
  ring?: boolean;
}) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [url]);

  const className = cx(
    'grid shrink-0 place-items-center overflow-hidden rounded-full',
    ring && 'outline outline-2 outline-offset-1 outline-signal',
  );

  if (url && !failed) {
    return (
      <img
        src={`${API_ORIGIN}${url}`}
        alt=""
        aria-hidden
        width={size}
        height={size}
        loading="lazy"
        decoding="async"
        // The avatar endpoint is authenticated, so a split-origin deployment has to send
        // the session cookie with the image request or every picture 401s.
        crossOrigin={API_ORIGIN ? 'use-credentials' : undefined}
        onError={() => setFailed(true)}
        className={cx(className, 'object-cover')}
        style={{ width: size, height: size }}
      />
    );
  }

  const hue = hashHue(userId);
  return (
    <span
      aria-hidden
      className={cx(className, 'font-medium')}
      style={{
        width: size,
        height: size,
        background: `linear-gradient(145deg, hsl(${hue} 42% 30%), hsl(${(hue + 40) % 360} 38% 18%))`,
        color: '#F5F6F7',
        fontSize: size * 0.4,
      }}
    >
      {initials(displayName)}
    </span>
  );
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return (parts[0] as string).slice(0, 2).toUpperCase();
  return `${(parts[0] as string)[0]}${(parts[parts.length - 1] as string)[0]}`.toUpperCase();
}

/** Stable hue from a user id, so a person's colour never changes between sessions. */
function hashHue(seed: string): number {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) % 360;
  }
  return hash;
}

export function LevelTag({ level }: { level: 'nfl' | 'cfb' }) {
  return (
    <span className="font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-field-500">
      {level === 'nfl' ? 'NFL' : 'College'}
    </span>
  );
}

/**
 * Where a game currently is, in the broadcast's own words.
 *
 * `detail` is preferred over anything we could assemble ourselves — the feed says
 * `Halftime`, `End of 3rd`, `Final/OT`, and every one of those is more accurate than a
 * quarter number we reformatted. The `Q3 4:21` fallback exists only for the rare event
 * that arrives without it.
 *
 * The dot breathes only while a clock is actually running. A paused game showing animated
 * motion is a small lie, and this is the one perpetual animation in the design.
 */
export function LiveStatus({ live }: { live: SlateGameDto['live'] }) {
  const running = live.phase === 'in' && Boolean(live.displayClock);
  const label =
    live.detail ??
    (live.period ? `Q${live.period}${live.displayClock ? ` ${live.displayClock}` : ''}` : 'Live');

  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={cx('size-1.5 shrink-0 rounded-full bg-live', running && 'taste-live-dot')} />
      <span className="font-mono text-[10px] font-medium uppercase tracking-[0.14em] text-live">
        {label}
      </span>
    </span>
  );
}

/**
 * A score, away first so it reads in the same order as the matchup beside it.
 *
 * Takes two numbers rather than a game, because the right source depends on the moment:
 * a running game's score lives on the scoreboard mirror, while a settled one lives on the
 * contest row — and that is the only copy that exists when a commissioner typed the final
 * in by hand. Renders nothing when either side is missing, so a game we have no score for
 * shows no score rather than a misleading `0-0`.
 */
export function ScoreLine({
  away,
  home,
  className,
}: {
  away: number | null;
  home: number | null;
  className?: string;
}) {
  if (away === null || home === null) return null;

  return (
    <span className={cx('font-mono tabular-nums', className)}>
      {away}
      <span className="mx-0.5 text-field-600">&ndash;</span>
      {home}
    </span>
  );
}

/**
 * The league mark: a check drawn in two strokes, the short one in the brand accent.
 *
 * Inlined rather than fetched from `/icons`, because it sits in the sticky header and a
 * separate request would let the header render brandless for a frame on every cold load.
 * The colours are literal hex on purpose — an `<svg>` fill cannot read a Tailwind class —
 * and are the palette's own field-850 / field-100 / signal.
 */
export function BrandMark({ className = 'size-7' }: { className?: string }) {
  return (
    <svg viewBox="0 0 32 32" className={cx('shrink-0', className)} aria-hidden>
      <rect width="32" height="32" rx="8" fill="#181C20" />
      <g strokeWidth="3.67">
        <path d="M15 20.67 L25 8.67" stroke="#F5F6F7" />
        <path d="M7.33 15.67 L12.67 21" stroke="#FF6B1A" />
      </g>
    </svg>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cx('taste-skeleton rounded-lg', className)} />;
}

/** Loading state that matches the shape of what is coming, not a spinner. */
export function SkeletonList({ rows = 5, height = 'h-24' }: { rows?: number; height?: string }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} className={cx('w-full', height)} />
      ))}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="rounded-card border border-loss/25 bg-loss/[0.06] p-6">
      <div className="flex items-start gap-3">
        <WarningOctagon size={20} weight="fill" className="mt-0.5 shrink-0 text-loss" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-field-100">That didn&apos;t load</p>
          <p className="mt-1 text-sm text-field-400">{message}</p>
          <Button variant="outline" size="sm" onClick={onRetry} className="mt-4">
            <ArrowClockwise size={15} weight="bold" />
            Try again
          </Button>
        </div>
      </div>
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
  icon,
}: {
  title: string;
  description: string;
  action?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className="rounded-card border border-dashed border-line px-6 py-14 text-center">
      {icon && <div className="mb-4 flex justify-center text-field-500">{icon}</div>}
      <p className="text-base font-medium text-field-100">{title}</p>
      <p className="mx-auto mt-2 max-w-[42ch] text-sm leading-relaxed text-field-400">
        {description}
      </p>
      {action && <div className="mt-6 flex justify-center">{action}</div>}
    </div>
  );
}

export function InlineNotice({
  tone = 'info',
  children,
}: {
  tone?: 'info' | 'warn' | 'success' | 'error';
  children: ReactNode;
}) {
  const tones = {
    info: 'border-line bg-field-800 text-field-200',
    warn: 'border-push/30 bg-push/10 text-push',
    success: 'border-win/30 bg-win/10 text-win',
    error: 'border-loss/30 bg-loss/10 text-loss',
  };
  const icons = {
    info: null,
    warn: <Warning size={16} weight="fill" className="mt-0.5 shrink-0" />,
    success: <CheckCircle size={16} weight="fill" className="mt-0.5 shrink-0" />,
    error: <WarningOctagon size={16} weight="fill" className="mt-0.5 shrink-0" />,
  };
  return (
    <div className={cx('flex items-start gap-2 rounded-xl border px-3 py-2.5 text-sm', tones[tone])}>
      {icons[tone]}
      <div className="min-w-0">{children}</div>
    </div>
  );
}

/** Big monospace figure with a label under it. Used for records, points, rank. */
export function Stat({
  value,
  label,
  accent,
}: {
  value: ReactNode;
  label: string;
  accent?: boolean;
}) {
  return (
    <div>
      <div
        className={cx(
          'font-mono text-2xl font-semibold tabular-nums leading-none',
          accent ? 'text-signal' : 'text-field-100',
        )}
      >
        {value}
      </div>
      <div className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-field-500">
        {label}
      </div>
    </div>
  );
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <label className="flex flex-col gap-2">
      <span className="font-mono text-[11px] font-medium uppercase tracking-[0.16em] text-field-400">
        {label}
      </span>
      {children}
      {hint && !error && <span className="text-xs text-field-500">{hint}</span>}
      {error && <span className="text-xs text-loss">{error}</span>}
    </label>
  );
}

export function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={cx(
        'h-12 w-full rounded-xl border border-line bg-field-900 px-3.5 text-field-100',
        'placeholder:text-field-500',
        'transition-colors duration-200 focus:border-signal focus:outline-none',
        props.className,
      )}
    />
  );
}

/**
 * How to install the app on an iPhone.
 *
 * Written once and used in both places that need it. It is a factual claim about somebody
 * else's software, of the kind that goes quietly out of date — and an install instruction
 * that is wrong is worse than none, because the reader concludes the app is broken rather
 * than that the steps are stale. Two copies of it would have drifted apart the first time
 * Apple moved a button, which is exactly what happened to the version this replaced.
 *
 * Recent Safari collapsed its toolbar: Share is no longer a top-level button, and "Add to
 * Home Screen" now sits behind "View More" inside the share sheet. The older layout is
 * covered by the note at the end rather than by a second list, since anyone still on it
 * will simply find the Share icon a step early and skip ahead.
 */
export function IosInstallSteps({ compact = false }: { compact?: boolean }) {
  const steps = [
    { icon: <DotsThree size={16} weight="bold" />, text: 'in the bottom right of Safari', lead: 'Tap' },
    { icon: <Export size={15} weight="bold" />, text: 'Share', lead: 'Tap' },
    { icon: <CaretCircleDown size={15} weight="bold" />, text: 'View More', lead: 'Tap' },
    { icon: <PlusSquare size={15} weight="bold" />, text: 'Add to Home Screen', lead: 'Tap' },
  ];

  return (
    <div className={compact ? 'text-[13px]' : 'text-sm'}>
      <ol className="space-y-1.5">
        {steps.map((step, index) => (
          <li key={step.text} className="flex items-center gap-2 text-field-300">
            <span className="w-3 shrink-0 font-mono text-[11px] text-field-600">{index + 1}</span>
            <span className="text-field-500">{step.lead}</span>
            <span className="text-signal">{step.icon}</span>
            <span className={index === 0 ? 'text-field-400' : 'font-medium'}>{step.text}</span>
          </li>
        ))}
      </ol>
      <p className="mt-2.5 pl-5 text-[12px] leading-relaxed text-field-600">
        On an older iPhone the Share icon sits in the toolbar directly and there is no
        &ldquo;View More&rdquo; — tap Share, then scroll to Add to Home Screen.
      </p>
    </div>
  );
}

/** Same skin as `Input`, for the fields where a sentence is the expected answer. */
export function Textarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      rows={3}
      {...props}
      className={cx(
        'w-full resize-y rounded-xl border border-line bg-field-900 px-3.5 py-3 text-field-100',
        'placeholder:text-field-500',
        'transition-colors duration-200 focus:border-signal focus:outline-none',
        props.className,
      )}
    />
  );
}

export function Select(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      {...props}
      className={cx(
        'h-11 rounded-xl border border-line bg-field-900 px-3 text-sm text-field-100',
        'transition-colors duration-200 focus:border-signal focus:outline-none',
        props.className,
      )}
    />
  );
}

/** Horizontal week selector — a strip rather than a dropdown, so it is one tap. */
export function WeekStrip({
  weeks,
  selectedId,
  onSelect,
}: {
  weeks: { id: string; weekNumber: number; slateStatus: string }[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  if (weeks.length === 0) return null;
  return (
    <div className="taste-scroll-x -mx-4 flex gap-1.5 px-4 pb-1">
      {[...weeks]
        .sort((a, b) => a.weekNumber - b.weekNumber)
        .map((week) => {
          const active = week.id === selectedId;
          return (
            <button
              key={week.id}
              onClick={() => onSelect(week.id)}
              style={{ scrollSnapAlign: 'center' }}
              className={cx(
                'shrink-0 rounded-lg px-3 py-2 font-mono text-xs font-medium tabular-nums',
                'transition-all duration-200 ease-[var(--ease-out-expo)] active:scale-[0.96]',
                active
                  ? 'bg-signal text-ink'
                  : week.slateStatus === 'draft'
                    ? 'border border-dashed border-line text-field-500'
                    : 'bg-field-800 text-field-300 hover:bg-field-700',
              )}
            >
              W{week.weekNumber}
            </button>
          );
        })}
    </div>
  );
}
